"use client";

import * as React from "react";

type RevealElement = "div" | "li" | "section";

interface RevealProps extends React.HTMLAttributes<HTMLElement> {
  as?: RevealElement;
  /** Start offsets in px and degrees; the element slides from here into place. */
  x?: number;
  y?: number;
  rotate?: number;
  scale?: number;
  /** Delay and duration in ms. */
  delay?: number;
  duration?: number;
}

let sharedObserver: IntersectionObserver | null = null;

// One observer serves every reveal on the page. Each element shows once and
// is then forgotten, so scrolling back up never replays it.
function getObserver(): IntersectionObserver {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.reveal = "shown";
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0 },
    );
  }
  return sharedObserver;
}

function toMotionStyle({
  x,
  y,
  rotate,
  scale,
  delay,
  duration,
}: Pick<
  RevealProps,
  "x" | "y" | "rotate" | "scale" | "delay" | "duration"
>): React.CSSProperties {
  const style: Record<string, string> = {};
  if (x !== undefined) style["--reveal-x"] = `${x}px`;
  if (y !== undefined) style["--reveal-y"] = `${y}px`;
  if (rotate !== undefined) style["--reveal-rotate"] = `${rotate}deg`;
  if (scale !== undefined) style["--reveal-scale"] = `${scale}`;
  if (delay !== undefined) style["--reveal-delay"] = `${delay}ms`;
  if (duration !== undefined) style["--reveal-duration"] = `${duration}ms`;
  return style as React.CSSProperties;
}

/**
 * Slides its content in the first time it scrolls into view (styles in
 * globals.css). Content that is already on screen when the page hydrates
 * stays where it is, and nothing is ever hidden in the server HTML.
 */
export default function Reveal({
  as = "div",
  x,
  y,
  rotate,
  scale,
  delay,
  duration,
  style,
  children,
  ...rest
}: RevealProps): React.ReactElement {
  const ref = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    // Effects run again when Next shows a route it kept hidden after
    // navigating away, often after scroll restoration. Whatever was already
    // waiting keeps waiting, without re-measuring where it now sits.
    const state = element.dataset.reveal;
    if (state === "shown") return;
    if (state !== "armed") {
      if (element.getBoundingClientRect().top < window.innerHeight) return;
      element.dataset.reveal = "armed";
    }

    const observer = getObserver();
    observer.observe(element);
    return () => observer.unobserve(element);
  }, []);

  return React.createElement(
    as,
    {
      ...rest,
      ref,
      style: { ...toMotionStyle({ x, y, rotate, scale, delay, duration }), ...style },
    },
    children,
  );
}
